import { sanitizeMarkup } from "../helper/sanitize";

class ChatInput implements ChatInputInterface {
    private ACTION_BAR_ATTRIBUTE = 'data-js-chat-actions';
    private EMPTY_CLASS = 'is-empty';
    private TYPING_DEBOUNCE_INTERVAL_MS = 1000;

    constructor(
        private container: HTMLDivElement,
        private input: HTMLElement,
        private sendButton: HTMLInputElement
    ) {
        this.setupContainerListener();
        this.setupInputListeners();
        this.updateEmptyState();
    }

    
    public subscribeToSend(callback: () => void): void {
        this.sendButton.addEventListener('click', (event) => {
            event.preventDefault();
            callback();
        });

        this.input.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                callback();
            }
        });
    }

    public subscribeToTyping(callback: () => void): void {
        let lastTypingEventTimestamp = 0;

        this.input.addEventListener('input', () => {
            const currentTimestamp = Date.now();

            if (currentTimestamp - lastTypingEventTimestamp < this.TYPING_DEBOUNCE_INTERVAL_MS) {
                return;
            }

            lastTypingEventTimestamp = currentTimestamp;
            callback();
        });
    }
    
    public get(): string {
        return this.input.innerHTML;
    }
    
    public clear(): void {
        this.input.innerHTML = '';
        this.updateEmptyState();
    }

    public disable(): void {
        this.input.setAttribute('contenteditable', 'false');
        this.sendButton.setAttribute('disabled', 'true');
    }

    public disableSend(): void {
        this.sendButton.setAttribute('disabled', 'true');
    }

    public enableSend(): void {
        this.sendButton.removeAttribute('disabled');
    }

    public enable(): void {
        this.input.setAttribute('contenteditable', 'true');
        this.sendButton.removeAttribute('disabled');
    }

    private setupInputListeners(): void {
        this.input.addEventListener('paste', (event) => {
            this.handlePaste(event);
        });

        this.input.addEventListener('input', () => {
            this.sanitizeEditableContent();
        });
    }

    private setupContainerListener() {
        this.container.addEventListener('click', (event) => {
            if ((event.target as HTMLElement).hasAttribute(this.ACTION_BAR_ATTRIBUTE)) {
                event.preventDefault();
                this.input.focus();

                const range = document.createRange();
                range.selectNodeContents(this.input);
                range.collapse(false);

                const selection = window.getSelection();
                selection?.removeAllRanges();
                selection?.addRange(range);
            }
        });
    }

    private handlePaste(event: ClipboardEvent): void {
        event.preventDefault();

        const clipboardData = event.clipboardData;

        if (!clipboardData) {
            return;
        }

        const plainTextContent = clipboardData.getData('text/plain');
        const htmlContent = clipboardData.getData('text/html');
        const sanitizedContent = plainTextContent.length > 0
            ? this.convertPlainTextToMarkup(plainTextContent)
            : sanitizeMarkup(htmlContent);

        this.insertAtCaret(sanitizedContent);
        this.sanitizeEditableContent();
    }

    private sanitizeEditableContent(): void {
        const sanitizedMarkup = sanitizeMarkup(this.input.innerHTML);

        if (sanitizedMarkup === this.input.innerHTML) {
            this.updateEmptyState();
            return;
        }

        this.input.innerHTML = sanitizedMarkup;
        this.placeCaretAtEnd();
        this.updateEmptyState();
    }

    private updateEmptyState(): void {
        this.container.classList.toggle(this.EMPTY_CLASS, this.isEmpty());
    }

    private isEmpty(): boolean {
        const normalizedTextContent = (this.input.textContent ?? '')
            .replace(/\u00A0/g, ' ')
            .replace(/[\u200B-\u200D\uFEFF]/g, '')
            .trim();

        return normalizedTextContent.length === 0;
    }

    private insertAtCaret(html: string): void {
        const selection = window.getSelection();

        if (!selection || selection.rangeCount === 0 || !this.input.contains(selection.anchorNode)) {
            this.input.innerHTML += html;
            this.placeCaretAtEnd();
            return;
        }

        const range = selection.getRangeAt(0);
        range.deleteContents();

        const fragment = range.createContextualFragment(html);
        const lastInsertedNode = fragment.lastChild;
        range.insertNode(fragment);

        if (lastInsertedNode) {
            range.setStartAfter(lastInsertedNode);
            range.setEndAfter(lastInsertedNode);
            selection.removeAllRanges();
            selection.addRange(range);
        }
    }

    private placeCaretAtEnd(): void {
        const selection = window.getSelection();

        if (!selection) {
            return;
        }

        const range = document.createRange();
        range.selectNodeContents(this.input);
        range.collapse(false);

        selection.removeAllRanges();
        selection.addRange(range);
    }

    private escapeHtml(content: string): string {
        const temporaryContainer = document.createElement('div');
        temporaryContainer.textContent = content;

        return temporaryContainer.innerHTML;
    }

    private convertPlainTextToMarkup(content: string): string {
        return this.escapeHtml(content)
            .replace(/\r\n|\r|\n/g, '<br>');
    }
}

export default ChatInput;